/**
 * Generic renderer for TipTap's Suggestion plugin, mounted with Solid.
 *
 * Shared by the slash menu ("/") and wiki-link autocomplete ("[["):
 * a floating dropdown anchored to the caret with keyboard navigation
 * (↑/↓/Enter/Esc) and click.
 */
import { createSignal, For, type JSX, Show } from "solid-js";
import { render } from "solid-js/web";
import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";

const MENU_MARGIN_PX = 6;
const VIEWPORT_PADDING_PX = 8;
const MENU_WIDTH_PX = 280;
const FALLBACK_MENU_HEIGHT_PX = 240;

interface SuggestionRendererConfig<TItem> {
  /** Renders one menu row. */
  renderItem: (item: TItem, isSelected: boolean) => JSX.Element;
  /** Text when there are no results; if omitted, the menu stays empty. */
  emptyLabel?: string;
}

/**
 * Creates the `render` function that `Suggestion({ render })` consumes.
 */
export function createSuggestionRenderer<TItem>(config: SuggestionRendererConfig<TItem>) {
  return () => {
    const [items, setItems] = createSignal<TItem[]>([]);
    const [selectedIndex, setSelectedIndex] = createSignal(0);
    let command: ((item: TItem) => void) | null = null;
    let container: HTMLDivElement | null = null;
    let dispose: (() => void) | null = null;
    let hidden = false;

    const reposition = (rect: DOMRect | null) => {
      if (!container || !rect) {
        return;
      }
      const menuHeight = container.offsetHeight || FALLBACK_MENU_HEIGHT_PX;
      const below = rect.bottom + MENU_MARGIN_PX;
      const top =
        below + menuHeight > window.innerHeight
          ? Math.max(VIEWPORT_PADDING_PX, rect.top - menuHeight - MENU_MARGIN_PX)
          : below;
      const left = Math.max(
        VIEWPORT_PADDING_PX,
        Math.min(rect.left, window.innerWidth - MENU_WIDTH_PX - VIEWPORT_PADDING_PX),
      );
      container.style.top = `${top}px`;
      container.style.left = `${left}px`;
    };

    const selectItem = (index: number) => {
      const item = items()[index];
      if (item && command) {
        command(item);
      }
    };

    const destroy = () => {
      dispose?.();
      dispose = null;
      container?.remove();
      container = null;
    };

    return {
      onStart: (payload: SuggestionProps<TItem, TItem>) => {
        hidden = false;
        command = (item: TItem) => {
          payload.command(item);
        };
        setItems(() => payload.items);
        setSelectedIndex(0);

        container = document.createElement("div");
        container.className = "suggestion-menu";
        document.body.append(container);

        dispose = render(
          () => (
            <Show
              when={items().length > 0}
              fallback={
                <Show when={config.emptyLabel}>
                  <div class="suggestion-menu-empty">{config.emptyLabel}</div>
                </Show>
              }
            >
              <ul class="suggestion-menu-list" role="listbox">
                <For each={items()}>
                  {(item, index) => (
                    <li role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={index() === selectedIndex()}
                        class="suggestion-menu-item"
                        classList={{ "is-selected": index() === selectedIndex() }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectItem(index());
                        }}
                        onMouseEnter={() => setSelectedIndex(index())}
                      >
                        {config.renderItem(item, index() === selectedIndex())}
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          ),
          container,
        );
        reposition(payload.clientRect?.() ?? null);
      },

      onUpdate: (payload: SuggestionProps<TItem, TItem>) => {
        command = (item: TItem) => {
          payload.command(item);
        };
        setItems(() => payload.items);
        setSelectedIndex((current) => Math.min(current, Math.max(0, payload.items.length - 1)));
        reposition(payload.clientRect?.() ?? null);
      },

      onKeyDown: (payload: SuggestionKeyDownProps): boolean => {
        if (hidden) {
          return false;
        }
        const count = items().length;
        switch (payload.event.key) {
          case "ArrowDown": {
            if (count > 0) {
              setSelectedIndex((current) => (current + 1) % count);
            }
            return true;
          }
          case "ArrowUp": {
            if (count > 0) {
              setSelectedIndex((current) => (current - 1 + count) % count);
            }
            return true;
          }
          case "Enter": {
            if (count > 0) {
              selectItem(selectedIndex());
              return true;
            }
            return false;
          }
          case "Escape": {
            hidden = true;
            destroy();
            return true;
          }
          default: {
            return false;
          }
        }
      },

      onExit: () => {
        destroy();
      },
    };
  };
}
